import React, {useEffect, useRef} from "react";
import {TextField, Box, Checkbox, FormControlLabel, MenuItem, Select, Typography, ListItemText} from "@mui/material";
import {useTranslation} from "react-i18next";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "./quill-overrides.css";
import {COMPANY_MEMBER_ROLES} from "../../../utils/enumItemsUtils";
import {CompanyMemberRole} from "../../../types/domain/companyMember";
import {NotificationStatus} from "../../../types/domain/notification";
import {DateTimePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";

import utc from "dayjs/plugin/utc";
import {useCompanies} from "../../../hooks/companiesHooks";

dayjs.extend(utc);

interface AddAnnouncementProps {
    id?: string;
    name: string;
    setName: (name: string) => void;
    content: string;
    setContent: (content: string) => void;
    selectedCompanyIds: string[];
    setSelectedCompanyIds: (ids: string[]) => void;
    allCompanies: boolean;
    setAllCompanies: (values: boolean) => void;
    setSelectedCompanyMemberRoles: (roles: CompanyMemberRole[]) => void;
    selectedCompanyMemberRoles: CompanyMemberRole[];
    status: NotificationStatus;
    setStatus: (status: NotificationStatus) => void;
    setStartTime: (date: string) => void;
    startTime: string | null;
}

const AddAnnouncement: React.FC<AddAnnouncementProps> = ({
    name,
    setName,
    content,
    setContent,
    selectedCompanyIds,
    setSelectedCompanyIds,
    allCompanies,
    setAllCompanies,
    setSelectedCompanyMemberRoles,
    selectedCompanyMemberRoles,
    status,
    setStatus,
    setStartTime,
    startTime,
}) => {
    const companies = useCompanies();
    const notificationStatuses = Object.values(NotificationStatus);
    const {t} = useTranslation();
    const editorRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<Quill | null>(null);

    useEffect(() => {
        if (editorRef.current && !quillRef.current) {
            quillRef.current = new Quill(editorRef.current, {
                theme: "snow",
                modules: {
                    toolbar: [["bold", "italic", "underline"], ["link"], [{list: "bullet"}]],
                },
            });

            quillRef.current.on("text-change", () => {
                const html = editorRef.current?.querySelector(".ql-editor")?.innerHTML ?? "";
                setContent(html);
            });

            if (content) {
                quillRef.current.root.innerHTML = content;
            }
        }
    }, [content, setContent]);

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: 2, margin: "0 auto"}}>
            <Box>
                <Typography variant="h4" sx={{mb: 1}}>
                    {t("add_announcement.message")}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t("add_announcement.name")}
                </Typography>

                <Box sx={{mb: 2}}>
                    <TextField variant="outlined" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
                </Box>

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t("add_announcement.content")}
                </Typography>

                <Box
                    ref={editorRef}
                    sx={{
                        marginTop: "2px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        backgroundColor: "#fff",
                        color: "#000",
                        fontFamily: "sans-serif",
                    }}
                />
            </Box>

            <Box sx={{mt: 2}}>
                <Typography variant="h4" sx={{mb: 1}}>
                    {t("add_announcement.audience")}
                </Typography>

                <FormControlLabel
                    control={<Checkbox checked={allCompanies} onChange={(e) => setAllCompanies(e.target.checked)} />}
                    label={t("add_announcement.send_announcement_to_all_users")}
                />

                <br />

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t("add_announcement.companies")}
                </Typography>

                <Select
                    multiple
                    value={selectedCompanyIds}
                    onChange={(event) => {
                        const selectedCompanyIds = event.target.value as string[];
                        setSelectedCompanyIds(selectedCompanyIds);
                    }}
                    renderValue={(selected) => {
                        return `${selected.length} ${t("add_announcement.selected")}`;
                    }}
                    sx={{width: "100%"}}
                >
                    {companies.isSuccess
                        ? companies.data.map((company) => (
                              <MenuItem
                                  key={company.id}
                                  value={company.id}
                                  sx={{height: 36, minHeight: 36, paddingY: 0.5}}
                              >
                                  <Checkbox checked={selectedCompanyIds.includes(company.id)} />
                                  <ListItemText primary={company?.properties?.name} />
                              </MenuItem>
                          ))
                        : ""}
                </Select>

                <br />
                <br />

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t("add_announcement.roles")}
                </Typography>

                <Select
                    multiple
                    value={selectedCompanyMemberRoles}
                    onChange={(event) => {
                        const selectedCompanyMemberRoles = event.target.value as CompanyMemberRole[];
                        setSelectedCompanyMemberRoles(selectedCompanyMemberRoles);
                    }}
                    renderValue={(selected) => {
                        return `${selected.length} ${t("add_announcement.selected")}`;
                    }}
                    sx={{width: "100%"}}
                >
                    {COMPANY_MEMBER_ROLES.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                            <Checkbox checked={selectedCompanyMemberRoles.includes(role.id)} />
                            <ListItemText primary={CompanyMemberRole[role.id]} />
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            <Box sx={{mt: 2}}>
                <Typography variant="h4" sx={{mb: 1}}>
                    {t("add_announcement.status")}
                </Typography>

                <Select
                    size="small"
                    value={status}
                    onChange={(event) => {
                        setStatus(event.target.value as NotificationStatus);
                    }}
                    displayEmpty
                    sx={{width: "100%"}}
                >
                    {notificationStatuses.map((status) => (
                        <MenuItem key={status} value={status}>
                            {status}
                        </MenuItem>
                    ))}
                </Select>

                <br />
                <br />

                {status === NotificationStatus.SCHEDULED ? (
                    <DateTimePicker
                        sx={{width: "100%"}}
                        label={t("add_announcement.start_time_UTC")}
                        value={dayjs(startTime)}
                        onChange={(newValue) => {
                            if (newValue) {
                                if (newValue) {
                                    const formatted = newValue.format("YYYY-MM-DDTHH:mm:ss.SSS");
                                    setStartTime(formatted);
                                }
                            }
                        }}
                    />
                ) : (
                    ""
                )}
            </Box>
        </Box>
    );
};

export default AddAnnouncement;
